/**
 * 게임 전역 상수.
 * 밸런스와 점수 공식은 전부 여기 모아뒀다. 다른 파일을 고치지 말고 여기만 바꾼다.
 */
export const CONFIG = {
  // ── 화면 ────────────────────────────────────────────────
  BG_COLOR: 0x0d0d14,
  GRID_COLOR: 0x1b1b2a,
  /**
   * 판정용 표준 화면 크기. 적이 나오는 거리·멀어진 적을 되돌리는 거리·맵 픽업을 놓는 거리는
   * 실제 창 크기 대신 이 값으로 정한다 — 폰이든 큰 모니터든 같은 판이 되게(점수 공정성).
   * 예전에는 실제 창 크기를 써서, 큰 화면일수록 적이 멀리서 나와 경험치를 덜 줍고 조합이 늦었다.
   */
  VIEW_W: 1280,
  VIEW_H: 720,
  /** 적이 나타날 때 투명 → 불투명에 걸리는 시간(초). 표준보다 큰 화면에서는 화면 안에서 생기므로 가린다. */
  ENEMY_FADE_IN: 0.4,

  // ── 루프 ────────────────────────────────────────────────
  FIXED_DT: 1 / 60, // 로직은 항상 60Hz 고정 스텝으로 돈다
  MAX_STEPS_PER_FRAME: 5, // 렉이 걸려도 한 프레임에 5스텝까지만 (스파이럴 방지)

  // ── 한 판 ───────────────────────────────────────────────
  // 끝이 없다. 죽을 때까지 버티는 게 전부다.
  // 이 값은 시뮬레이터가 무한 루프에 빠지지 않게 하는 상한일 뿐, 게임은 여기서 안 끝난다.
  SIM_MAX_SECONDS: 3600,

  // ── 성능 상한 (모바일 기준) ──────────────────────────────
  MAX_ENEMIES: 600,
  MAX_PROJECTILES: 400,
  MAX_GEMS: 500,
  MAX_FIELDS: 64, // 잿불 자국(회광반조·불길 도배·불바다)이 잠깐씩 여럿 깔린다
  MAX_ENEMY_SHOTS: 140,
  SPATIAL_CELL: 72, // 공간 분할 격자 한 칸 크기(px)

  // ── 플레이어 ────────────────────────────────────────────
  PLAYER_RADIUS: 13,
  PLAYER_IFRAME: 0.45, // 피격 후 무적 시간(초)
  PICKUP_RADIUS: 110, // 보석 흡수 시작 거리
  GEM_MAGNET_ACCEL: 1400,
  GEM_MAGNET_MAX: 620,

  // ── 경험치 곡선 ─────────────────────────────────────────
  // 레벨 L에서 다음 레벨까지 필요한 경험치
  xpToNext(level: number): number {
    // 초반은 쭉쭉, 첫 조합(약 6분, Lv30~40) 뒤로는 급하게 비싸진다.
    //
    // 적 수도 등급도 시간이 갈수록 오르기 때문에 경험치 수급은 알아서 불어난다.
    // 보석 경험치를 버리지 않게 된 뒤로(game.ts dropGem)는 5~10분 사이에 1분마다 두 배꼴로 불어나서,
    // 제곱 항만으로는 두 번째 조합이 첫 조합 2~3분 뒤에 바로 왔다.
    // 그래서 Lv30 부터 세제곱 항을 더했다. 보석을 주우러 다니는 플레이어 기준 첫 조합 약 6분, 두 번째 약 12분 반
    // (README '레벨업은 갈수록 느려진다').
    const L = level;
    return Math.floor(2.5 * (4 + L * 2 + Math.pow(L, 2.8) * 0.05 + 1.2 * Math.pow(Math.max(0, L - 30), 3)));
  },

  // ── 적 스케일링 (t = 경과 시간 초) ───────────────────────
  enemyHpScale(t: number): number {
    // 끝이 없는 판이라 후반에는 적이 플레이어보다 빨리 커져야 한다.
    // 완만하게 두면 조합 스킬을 모은 뒤로 영영 안 죽는다.
    return 1 + t / 120 + Math.pow(t / 300, 2.4);
  },
  enemyDamageScale(t: number): number {
    // 1.11 난이도 A: 예전 1 + t/300 은 4분에 1.8배라, 감시자·첫 보스·팽창체가 한꺼번에 오는 4~5분이 절벽이었다.
    // 증가 속도를 절반으로 (README '난이도').
    return 1 + t / 600;
  },
  enemySpeedScale(t: number): number {
    return 1 + t / 1600;
  },
  // 스폰 간격(초)과 한 번에 나오는 수
  spawnInterval(t: number): number {
    return Math.max(0.08, 0.7 - t / 900);
  },
  spawnBatch(t: number): number {
    return 1 + Math.floor(t / 100);
  },

  // ── 1.23 잡몹 단계 — 뒤로 갈수록 수만 늘지 않고 잡몹 자체가 세진다 ──
  // 새로 나오는 잡몹은 나온 시각의 단계를 단다(이미 나와 있던 놈은 그대로). 10분에 2단계, 그 뒤 6분마다 한 단계.
  // 단계마다 조금 크고 단단하고 아프고 빨라지며 단계 색으로 빛나고, 행동이 하나씩 세진다(game.ts — 이어 덮치기·하트 여럿·
  // 부채꼴 장풍·더 쪼개짐·두 번 할퀴기·웅크렸다 달려들기). 대신 한 번에 나오는 수를 그만큼 줄인다
  MOB_TIER_START: 600,
  MOB_TIER_EVERY: 360,
  MOB_TIER_MAX: 6,
  mobTier(t: number): number {
    if (t < CONFIG.MOB_TIER_START) return 1;
    return Math.min(CONFIG.MOB_TIER_MAX, 2 + Math.floor((t - CONFIG.MOB_TIER_START) / CONFIG.MOB_TIER_EVERY));
  },
  /** 단계 배율 — k = 단계 − 1 */
  mobTierMul(tier: number): { hp: number; damage: number; speed: number; size: number; xp: number; count: number } {
    const k = Math.max(0, tier - 1);
    return { hp: 1 + 0.12 * k, damage: 1 + 0.06 * k, speed: 1 + 0.03 * k, size: 1 + 0.03 * k, xp: 1 + 0.18 * k, count: 1 + 0.3 * k };
  },
  /**
   * 동시에 살아 있을 수 있는 잡몹 수 — 단계가 오를수록 줄인다.
   * 이걸 안 줄이면 "한 마리가 세짐"이 그대로 난이도 폭등이 된다(잘 안 죽어 곁에 쌓이고, 부딪힘이 그만큼 는다):
   * 단계만 넣고 재 보니 30분 분당 피해가 1.7천 → 4천이었다. 수를 줄여야 "수보다 질"이 된다.
   */
  mobCap(t: number): number {
    const k = Math.max(0, CONFIG.mobTier(t) - 1);
    return Math.round(CONFIG.MAX_ENEMIES / (1 + 0.22 * k));
  },
  /** 단계 색 (2단계부터 — 몸 위에 은은하게 더한다. 엘리트 금빛과 겹치지 않게) */
  MOB_TIER_COLORS: [0, 0, 0x6fd3ff, 0xa77dff, 0xff5e7a, 0xff3df0, 0xe8f0ff] as readonly number[],

  // ── 상태효과 (살바트리스 키워드를 실시간으로 옮긴 값) ──
  STATUS: {
    // [화염]: 주기마다 "중첩 × 한 중첩 피해" 를 입고 중첩에 BURN_DECAY 를 곱한다(0 = 전부 사라짐, burnLock 중에는 안 준다).
    // 한 중첩 피해 = 불을 건 스킬 한 방 공격력 × 그 스킬의 화염 배수(WeaponDef.burnMul, tools/levels_design.py BURN).
    // BURN_DAMAGE_PER_STACK 은 화염 배수가 없는 불(예전 방식)에만 쓰는 고정값이다.
    BURN_INTERVAL: 1.6,
    BURN_DAMAGE_PER_STACK: 7,
    BURN_DECAY: 0,
    BURN_MAX: 40,

    // [서리]: 쌓이면 [빙결]. 빙결 중에는 못 움직이고 받는 피해가 늘어난다
    // 원작은 10중첩이지만 실시간에서는 9 언저리에서 계속 막혔다. 8로 내려 실제로 얼게 만든다.
    FROST_FREEZE_AT: 8,
    FROST_DECAY_PER_SEC: 0.6,
    FROST_SLOW_PER_STACK: 0.045, // 중첩당 둔화율 (최대 FROST_SLOW_MAX)
    FROST_SLOW_MAX: 0.45,
    FREEZE_DURATION: 1.4,
    FREEZE_DAMAGE_MUL: 1.2,
    FROST_IMMUNE_AFTER: 1.2,

    // [감전]: 한 번에 큰 피해를 받으면 주변으로 터지고 사라진다
    SHOCK_TRIGGER: 18,
    SHOCK_SPLASH_RATIO: 0.45,
    SHOCK_RADIUS: 80,
  },

  // ── 맵에 떨어지는 픽업 ──────────────────────────────────
  // 가만히 있으면 안 되게 만드는 장치다. 보러 가야 얻는다.
  PICKUP: {
    /** 체력 회복 구슬 — 드문드문, 아주 눈에 띄게 */
    HEAL_INTERVAL: [24, 36] as [number, number],
    HEAL_RATIO: 0.3, // 최대 체력의 30%
    HEAL_MAX_ON_MAP: 2,

    /** 잔 경험치 — 계속 조금씩 */
    XP_INTERVAL: [3.5, 6] as [number, number],
    XP_VALUE: 5,
    XP_MAX_ON_MAP: 14,

    /** 괜찮은 경험치 — 가끔 */
    XP_BIG_INTERVAL: [26, 42] as [number, number],
    XP_BIG_VALUE: 55,

    /** 큰 경험치 — 후반에만, 아주 드물게 */
    XP_HUGE_AFTER: 300,
    XP_HUGE_INTERVAL: [75, 120] as [number, number],
    XP_HUGE_VALUE: 320,

    /** 플레이어로부터 이만큼 떨어진 곳에 놓는다 */
    SPAWN_MIN: 190,
    SPAWN_MARGIN: 70, // 표준 화면(VIEW_W×VIEW_H) 가장자리에서 안쪽으로
  },

  // ── 보스 ────────────────────────────────────────────────
  // 일정 간격으로 계속 나온다. 목록을 한 바퀴 돌면 더 단단해져서 다시 나온다.
  BOSS_INTERVAL: 240,
  /**
   * n번째 보스(0부터)의 체력 배율 — 그 보스가 처음 나온 때보다 일반 적 체력(enemyHpScale)이 는 만큼.
   * 1.22 전에는 바퀴마다 1 + 1.6 × 바퀴 만 붙여서, 일반 적이 77배가 되는 28분에 파수꾼(4.2배)이 3~8초 만에 죽었다
   * — 30분 무렵 보스가 기술을 몇 번 못 쓰고 죽어 패턴이 안 보였다. 첫 바퀴는 1 그대로
   */
  bossHpScale(index: number, bossCount: number): number {
    const n = Math.max(1, bossCount);
    const lap = Math.floor(index / n);
    const first = ((index % n) + 1) * CONFIG.BOSS_INTERVAL;
    const now = (index + 1) * CONFIG.BOSS_INTERVAL;
    // 1.23: 잡몹 단계로 경험치가 늘어 플레이어가 더 세졌다 — 바퀴마다 조금 더 붙이지 않으면 2바퀴째 보스가 10초 만에 녹는다
    return (CONFIG.enemyHpScale(now) / CONFIG.enemyHpScale(first)) * (1 + 0.6 * lap);
  },

  // ── 점수 공식 ───────────────────────────────────────────
  // score = 생존초*PER_SECOND + 처치*PER_KILL + 레벨*PER_LEVEL
  SCORE: {
    PER_SECOND: 6,
    PER_KILL: 8,
    PER_LEVEL: 200,
  },

  // ── 슬롯 ────────────────────────────────────────────────
  MAX_WEAPONS: 5,
  MAX_PASSIVES: 5,
  LEVELUP_CHOICES: 3,
  /** 조합에 필요한 재료 무기 수 */
  FUSION_INGREDIENTS: 3,
  /**
   * 이 레벨에 닿은 무기부터 조합 재료가 된다(만렙이 이보다 낮으면 만렙).
   * 만렙(7·8)까지 기다리게 하면 약한 조합에서 두 번째 조합이 30분 넘게 안 열렸다.
   * 6이면 두 번째 진화 갈림길까지 고른 뒤에 합치게 된다. (README '레벨업은 갈수록 느려진다')
   */
  FUSION_MIN_LEVEL: 6,
} as const;

/** 점수 계산. 서버(2단계)에서도 같은 공식으로 검증한다. */
export function computeScore(survivalSec: number, kills: number, level: number): number {
  const s = CONFIG.SCORE;
  return Math.floor(survivalSec * s.PER_SECOND + kills * s.PER_KILL + level * s.PER_LEVEL);
}
